import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/App";
import { motion, AnimatePresence } from "framer-motion";
import { Sun, Moon, ArrowLeft, Trophy, ChevronDown, ChevronUp } from "lucide-react";

// ── Dark mode hook ────────────────────────────────────────────────────────────
function useDarkMode() {
  const [dark, setDark] = useState(() => {
    try { return localStorage.getItem("tg-theme") === "dark"; } catch { return false; }
  });
  const toggle = () => setDark(prev => {
    const next = !prev;
    try { localStorage.setItem("tg-theme", next ? "dark" : "light"); } catch {}
    return next;
  });
  return [dark, toggle];
}

const initials = (name) => name ? name.charAt(0).toUpperCase() : "?";

const LEADERBOARD_TOPICS = [
  { id: "all",               label: "🌐 All Topics",              active: true  },
  { id: "indian-income-tax", label: "📘 Income Tax (Old vs New)", active: true  },
  { id: "gst",               label: "🧾 GST",                     active: false },
  { id: "tds-tcs",           label: "💼 TDS & TCS",               active: false },
  { id: "capital-gains",     label: "📈 Capital Gains",           active: false },
  { id: "corporate-tax",     label: "🏢 Corporate Tax",           active: false },
];

const MEDALS = [
  { gradient: "linear-gradient(145deg,#f59e0b,#d97706)", ring: "#fbbf24", label: "👑", height: 130 },
  { gradient: "linear-gradient(145deg,#94a3b8,#64748b)", ring: "#cbd5e1", label: "🥈", height: 100 },
  { gradient: "linear-gradient(145deg,#b45309,#92400e)", ring: "#d97706", label: "🥉", height: 80  },
];
const PODIUM_ORDER = [1, 0, 2]; // 2nd, 1st, 3rd avatar positions

const LeaderboardPage = () => {
  const navigate = useNavigate();
  const { token, user } = useAuth();
  const [D, toggle] = useDarkMode();
  const [period, setPeriod] = useState("daily");
  const [selectedTopic, setSelectedTopic] = useState("all");
  const [leaderboard, setLeaderboard] = useState([]);
  const [loading, setLoading] = useState(true);
  const [records, setRecords] = useState([]);
  const [recordsLoading, setRecordsLoading] = useState(false);
  const [expandedRecord, setExpandedRecord] = useState(null);

  // Date search state
  const todayISO = new Date().toISOString().slice(0, 10); // "2026-03-31"
  const curMonthISO = new Date().toISOString().slice(0, 7); // "2026-03"
  const [dateSearch, setDateSearch] = useState(todayISO);
  const [monthSearch, setMonthSearch] = useState(curMonthISO);
  const [searchRecords, setSearchRecords] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchPerformed, setSearchPerformed] = useState(false);

  useEffect(() => {
    fetchLeaderboard(period, selectedTopic);
    // Reset search state when period changes
    setSearchRecords([]);
    setSearchPerformed(false);
  }, [period, selectedTopic]);
  useEffect(() => { fetchRecords(period); }, [period]);

  const fetchLeaderboard = async (p, topicId = "all") => {
    setLoading(true);
    try {
      const url = `${process.env.REACT_APP_BACKEND_URL}/api/leaderboard/${p}${topicId !== "all" ? `?topic_id=${topicId}` : ""}`;
      const r = await fetch(url, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (r.ok) {
        const data = await r.json();
        setLeaderboard(Array.isArray(data) ? data : (data.leaderboard || []));
      }
    } catch {}
    finally { setLoading(false); }
  };

  const fetchRecords = async (p) => {
    setRecordsLoading(true);
    try {
      const r = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/leaderboard/records/${p}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (r.ok) {
        const data = await r.json();
        setRecords(data.records || []);
      }
    } catch {}
    finally { setRecordsLoading(false); }
  };

  // Search historical records for a specific date or month
  const searchByDate = async () => {
    const key = period === "daily" ? dateSearch : monthSearch;
    if (!key) return;
    setSearchLoading(true);
    setSearchRecords([]);
    setSearchPerformed(false);
    try {
      const r = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/leaderboard/records/${period}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (r.ok) {
        const data = await r.json();
        const allRecords = data.records || [];
        const matched = allRecords.filter(rec => rec.period_key === key);
        setSearchRecords(matched);
        setSearchPerformed(true);
      }
    } catch {}
    finally { setSearchLoading(false); }
  };

  // Colour tokens
  const bg       = D ? "#0f172a" : "#f0fdf4";
  const surface  = D ? "#1e293b" : "#ffffff";
  const surface2 = D ? "#253347" : "#f8fafc";
  const border   = D ? "#334155" : "#e2e8f0";
  const textPri  = D ? "#f1f5f9" : "#1e293b";
  const textSec  = D ? "#94a3b8" : "#64748b";

  // Deduplicate by user_id (safety net — backend $group should already guarantee this)
  const dedupedBoard = leaderboard.reduce((acc, entry) => {
    if (!acc.find(e => e.user_id === entry.user_id)) acc.push(entry);
    return acc;
  }, []);

  const displayLimit = period === "daily" ? 10 : 20;
  const topThree  = dedupedBoard.slice(0, 3);
  const restList  = dedupedBoard.slice(3, displayLimit); // 4th → 10th or 20th

  const formatPeriodLabel = (key) => {
    if (period === "daily") {
      const d = new Date(key);
      return d.toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" });
    }
    // monthly key = "2026-03"
    const [yr, mo] = key.split("-");
    return new Date(yr, mo - 1, 1).toLocaleDateString("en-IN", { month: "long", year: "numeric" });
  };

  return (
    <div style={{ minHeight: "100vh", background: bg, fontFamily: "'Nunito', sans-serif", transition: "background 0.3s" }}>

      {/* ── Header ── */}
      <header style={{ position: "sticky", top: 0, zIndex: 50, background: surface, borderBottom: `1px solid ${border}`, boxShadow: "0 1px 12px rgba(0,0,0,0.06)", transition: "background 0.3s" }}>
        <div style={{ maxWidth: 800, margin: "0 auto", padding: "0 20px", height: 60, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <button
            data-testid="back-to-dashboard-btn"
            onClick={() => navigate("/dashboard")}
            style={{ display: "flex", alignItems: "center", gap: 6, background: "none", border: "none", cursor: "pointer", color: textSec, fontSize: 14, fontWeight: 700, fontFamily: "'Nunito', sans-serif" }}
          >
            <ArrowLeft size={16} /> Dashboard
          </button>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <div style={{ width: 32, height: 32, background: "linear-gradient(135deg,#059669,#0d9488)", borderRadius: 10, display: "flex", alignItems: "center", justifyContent: "center" }}>
              <span style={{ color: "white", fontWeight: 900, fontSize: 16 }}>T</span>
            </div>
            <span style={{ fontWeight: 900, fontSize: 18, color: textPri }}>Leaderboard</span>
          </div>
          <button onClick={toggle} style={{ width: 36, height: 36, borderRadius: "50%", background: D ? "#334155" : "#f1f5f9", border: "none", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>
            {D ? <Sun size={16} color="#fbbf24" /> : <Moon size={16} color="#64748b" />}
          </button>
        </div>
      </header>

      <main style={{ maxWidth: 800, margin: "0 auto", padding: "28px 20px" }}>

        {/* ── Topic selector ── */}
        <div style={{ display: "flex", justifyContent: "center", marginBottom: 16 }}>
          <div style={{ position: "relative" }}>
            <select
              value={selectedTopic}
              onChange={e => setSelectedTopic(e.target.value)}
              style={{
                appearance: "none", WebkitAppearance: "none",
                background: surface, border: `1px solid ${border}`,
                color: textPri, borderRadius: 12, padding: "10px 36px 10px 16px",
                fontSize: 14, fontWeight: 700, cursor: "pointer", outline: "none",
                fontFamily: "'Nunito', sans-serif", minWidth: 240,
              }}
            >
              {LEADERBOARD_TOPICS.map(t => (
                <option key={t.id} value={t.id} disabled={!t.active}
                  style={{ background: surface, color: t.active ? textPri : "#64748b" }}>
                  {t.label}{!t.active ? " — Coming Soon" : ""}
                </option>
              ))}
            </select>
            <ChevronDown size={16} color={textSec} style={{ position: "absolute", right: 12, top: "50%", transform: "translateY(-50%)", pointerEvents: "none" }} />
          </div>
        </div>

        {/* ── Period Pills ── */}
        <div style={{ display: "flex", gap: 10, marginBottom: 28, justifyContent: "center" }}>
          {[
            { id: "daily",   label: "📅 Today" },
            { id: "monthly", label: "📆 This Month" },
          ].map(p => (
            <button
              key={p.id}
              data-testid={`tab-${p.id}`}
              onClick={() => setPeriod(p.id)}
              style={{
                padding: "10px 26px", borderRadius: 100, border: "none", cursor: "pointer",
                fontWeight: 800, fontSize: 14, fontFamily: "'Nunito', sans-serif",
                background: period === p.id ? "linear-gradient(135deg,#059669,#0d9488)" : surface,
                color: period === p.id ? "white" : textSec,
                boxShadow: period === p.id ? "0 4px 16px rgba(16,185,129,0.3)" : `0 1px 4px rgba(0,0,0,0.06)`,
                transition: "all 0.2s",
              }}
            >
              {p.label}
            </button>
          ))}
        </div>

        {/* ── Date / Month Search ── */}
        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.05 }}
          style={{ background: surface, border: `1px solid ${border}`, borderRadius: 16, padding: "16px 20px", marginBottom: 24, display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap", transition: "background 0.3s" }}>
          <span style={{ fontSize: 13, fontWeight: 700, color: textSec, flexShrink: 0 }}>
            {period === "daily" ? "📅 Search by date:" : "📆 Search by month:"}
          </span>
          {period === "daily" ? (
            <input
              type="date"
              value={dateSearch}
              max={todayISO}
              onChange={e => setDateSearch(e.target.value)}
              style={{ background: surface, border: `1px solid ${border}`, color: textPri, borderRadius: 8, padding: "7px 12px", fontSize: 14, fontFamily: "'Nunito', sans-serif", outline: "none" }}
            />
          ) : (
            <input
              type="month"
              value={monthSearch}
              max={curMonthISO}
              onChange={e => setMonthSearch(e.target.value)}
              style={{ background: surface, border: `1px solid ${border}`, color: textPri, borderRadius: 8, padding: "7px 12px", fontSize: 14, fontFamily: "'Nunito', sans-serif", outline: "none" }}
            />
          )}
          <button
            onClick={searchByDate}
            disabled={searchLoading}
            style={{ background: "linear-gradient(135deg,#059669,#0d9488)", color: "white", border: "none", borderRadius: 8, padding: "8px 20px", fontWeight: 800, fontSize: 13, cursor: "pointer", fontFamily: "'Nunito', sans-serif", opacity: searchLoading ? 0.7 : 1 }}
          >
            {searchLoading ? "Searching…" : "Search"}
          </button>
          {searchPerformed && (
            <button
              onClick={() => { setSearchRecords([]); setSearchPerformed(false); }}
              style={{ background: "none", border: `1px solid ${border}`, borderRadius: 8, padding: "7px 14px", color: textSec, fontWeight: 700, fontSize: 13, cursor: "pointer", fontFamily: "'Nunito', sans-serif" }}
            >
              Clear
            </button>
          )}
        </motion.div>

        {/* ── Date Search Results ── */}
        {searchPerformed && searchRecords.length === 0 && !searchLoading && (
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} style={{ marginBottom: 28 }}>
            <div style={{ background: surface, border: `1px solid ${border}`, borderRadius: 18, padding: 32, textAlign: "center", color: textSec, transition: "background 0.3s" }}>
              <div style={{ fontSize: 40, marginBottom: 10 }}>🔍</div>
              <p style={{ fontWeight: 700, color: textPri, marginBottom: 4 }}>No records found</p>
              <p style={{ fontSize: 13 }}>No leaderboard data recorded for this {period === "daily" ? "date" : "month"} yet.</p>
            </div>
          </motion.div>
        )}
        {searchPerformed && searchRecords.length > 0 && (
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} style={{ marginBottom: 28 }}>
            <div style={{ background: surface, border: `1px solid ${border}`, borderRadius: 18, overflow: "hidden", transition: "background 0.3s" }}>
              {searchRecords.map(rec => (
                <div key={rec.period_key}>
                  <div style={{ padding: "14px 20px", borderBottom: `1px solid ${border}`, display: "flex", alignItems: "center", gap: 10 }}>
                    <span style={{ fontSize: 18 }}>{period === "daily" ? "📅" : "📆"}</span>
                    <div>
                      <div style={{ fontSize: 15, fontWeight: 800, color: textPri }}>{formatPeriodLabel(rec.period_key)}</div>
                      <div style={{ fontSize: 12, color: textSec }}>{rec.top3?.length || 0} winners recorded</div>
                    </div>
                  </div>
                  <div style={{ padding: "12px 20px", display: "flex", flexDirection: "column", gap: 8 }}>
                    {(rec.top3 || []).length === 0
                      ? <p style={{ fontSize: 13, color: textSec, textAlign: "center", padding: "8px 0" }}>No participants this period</p>
                      : (rec.top3 || []).map((entry, i) => (
                        <div key={entry.user_id || i} style={{ display: "flex", alignItems: "center", gap: 12, padding: "10px 12px", background: D ? "#253347" : "#f8fafc", borderRadius: 12 }}>
                          <div style={{ width: 36, height: 36, borderRadius: "50%", background: MEDALS[i]?.gradient || "#64748b", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 16, color: "white", fontWeight: 900, flexShrink: 0 }}>
                            {initials(entry.name)}
                          </div>
                          <div style={{ flex: 1 }}>
                            <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                              <span style={{ fontSize: 14, fontWeight: 700, color: textPri }}>{entry.name}</span>
                              <span style={{ fontSize: 13 }}>{MEDALS[i]?.label}</span>
                            </div>
                            <span style={{ fontSize: 12, color: textSec }}>Rank #{entry.rank}</span>
                          </div>
                          <p style={{ fontSize: 14, fontWeight: 800, color: "#f59e0b", margin: 0 }}>⚡ {entry.total_gp} GP</p>
                        </div>
                      ))
                    }
                  </div>
                </div>
              ))}
            </div>
          </motion.div>
        )}

        {/* ── Live Top 3 Podium ── */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} style={{ marginBottom: 40 }}>
          <div style={{ background: surface, border: `1px solid ${border}`, borderRadius: 24, padding: "32px 24px 24px", textAlign: "center", transition: "background 0.3s" }}>
            {/* Title */}
            <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 8, marginBottom: 28 }}>
              <Trophy size={22} color="#f59e0b" />
              <span style={{ fontSize: 18, fontWeight: 900, color: textPri }}>
                {period === "daily" ? `Today's Top ${displayLimit}` : `This Month's Top ${displayLimit}`}
              </span>
              <Trophy size={22} color="#f59e0b" />
            </div>

            {loading ? (
              <div style={{ display: "flex", justifyContent: "center", alignItems: "flex-end", gap: 28, height: 200 }}>
                {[100, 140, 80].map((h, i) => (
                  <div key={i} style={{ width: 90, height: h, background: D ? "#253347" : "#f1f5f9", borderRadius: "12px 12px 0 0", animation: "pulse 1.4s ease-in-out infinite" }} />
                ))}
              </div>
            ) : topThree.length === 0 ? (
              <div style={{ padding: "40px 0", color: textSec }}>
                <div style={{ fontSize: 52, marginBottom: 12 }}>🏆</div>
                <p style={{ fontSize: 16, fontWeight: 700, color: textPri, marginBottom: 6 }}>No Rankings Yet</p>
                <p style={{ fontSize: 14 }}>Be the first to take a quiz and claim the top spot!</p>
                <button
                  data-testid="start-quiz-btn"
                  onClick={() => navigate("/dashboard")}
                  style={{ marginTop: 18, background: "linear-gradient(135deg,#059669,#0d9488)", color: "white", border: "none", borderRadius: 100, padding: "11px 28px", fontWeight: 800, cursor: "pointer", fontSize: 14, fontFamily: "'Nunito', sans-serif" }}
                >
                  Start Quiz →
                </button>
              </div>
            ) : (
              /* Podium layout: 2nd | 1st | 3rd */
              <div style={{ display: "flex", alignItems: "flex-end", justifyContent: "center", gap: 16 }}>
                {PODIUM_ORDER.map((idx) => {
                  const entry = topThree[idx];
                  if (!entry) return <div key={idx} style={{ width: 100 }} />;
                  const m = MEDALS[idx];
                  const isMe = entry.user_id === user?.id;
                  const isFirst = idx === 0;
                  return (
                    <motion.div
                      key={entry.user_id}
                      initial={{ opacity: 0, y: 30 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: idx === 0 ? 0.15 : idx === 1 ? 0 : 0.3 }}
                      style={{ display: "flex", flexDirection: "column", alignItems: "center", width: isFirst ? 120 : 100 }}
                    >
                      {/* Medal emoji */}
                      <div style={{ fontSize: isFirst ? 28 : 22, marginBottom: 6 }}>{m.label}</div>

                      {/* Avatar */}
                      <div style={{
                        width: isFirst ? 72 : 58, height: isFirst ? 72 : 58,
                        borderRadius: "50%", background: m.gradient,
                        border: `3px solid ${m.ring}`,
                        boxShadow: isMe ? `0 0 0 3px #10b981` : `0 4px 16px rgba(0,0,0,0.2)`,
                        display: "flex", alignItems: "center", justifyContent: "center",
                        fontSize: isFirst ? 26 : 22, color: "white", fontWeight: 900,
                        marginBottom: 8,
                      }}>
                        {initials(entry.name)}
                      </div>

                      {/* Name */}
                      <p style={{ fontSize: isFirst ? 14 : 13, fontWeight: 800, color: textPri, marginBottom: 3, maxWidth: isFirst ? 110 : 90, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", textAlign: "center" }}>
                        {entry.name}{isMe && <span style={{ marginLeft: 4, fontSize: 10, background: "#10b981", color: "white", borderRadius: 6, padding: "1px 5px" }}>You</span>}
                      </p>

                      {/* GP */}
                      <p style={{ fontSize: 13, color: "#f59e0b", fontWeight: 800, marginBottom: 6 }}>⚡ {entry.total_gp} GP</p>

                      {/* Pedestal */}
                      <div style={{
                        width: "100%", height: m.height, background: m.gradient,
                        borderRadius: "10px 10px 0 0",
                        display: "flex", alignItems: "center", justifyContent: "center",
                        fontSize: isFirst ? 32 : 26, color: "rgba(255,255,255,0.9)", fontWeight: 900,
                      }}>
                        {entry.rank}
                      </div>
                    </motion.div>
                  );
                })}
              </div>
            )}
          </div>
        </motion.div>

        {/* ── Ranked list: 4th place onwards ── */}
        {!loading && restList.length > 0 && (
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.18 }}
            style={{ marginBottom: 32 }}>
            <div style={{ background: surface, border: `1px solid ${border}`, borderRadius: 20, overflow: "hidden", transition: "background 0.3s" }}>
              {restList.map((entry, i) => {
                const rank = i + 4; // starts at 4th
                const isMe = entry.user_id === user?.id;
                return (
                  <div key={entry.user_id || i}
                    style={{
                      display: "flex", alignItems: "center", gap: 14,
                      padding: "13px 20px",
                      borderBottom: i < restList.length - 1 ? `1px solid ${border}` : "none",
                      background: isMe ? (D ? "#0d2a1e" : "#f0fdf4") : "transparent",
                      transition: "background 0.2s",
                    }}
                  >
                    {/* Rank number */}
                    <span style={{ width: 28, textAlign: "center", fontSize: 13, fontWeight: 800, color: textSec, flexShrink: 0 }}>
                      #{rank}
                    </span>
                    {/* Avatar */}
                    <div style={{ width: 38, height: 38, borderRadius: "50%", background: "linear-gradient(135deg,#334155,#475569)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 15, color: "white", fontWeight: 900, flexShrink: 0 }}>
                      {initials(entry.name)}
                    </div>
                    {/* Name */}
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <span style={{ fontSize: 14, fontWeight: 700, color: textPri }}>
                        {entry.name}
                        {isMe && <span style={{ marginLeft: 6, fontSize: 10, background: "#10b981", color: "white", borderRadius: 6, padding: "1px 5px" }}>You</span>}
                      </span>
                    </div>
                    {/* GP */}
                    <span style={{ fontSize: 14, fontWeight: 800, color: "#f59e0b", flexShrink: 0 }}>⚡ {entry.total_gp} GP</span>
                  </div>
                );
              })}
            </div>
          </motion.div>
        )}

        {/* ── Historical Records ── */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 16 }}>
            <div style={{ width: 36, height: 36, background: D ? "#253347" : "#fef3c7", borderRadius: 10, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 18 }}>📚</div>
            <div>
              <h3 style={{ fontSize: 17, fontWeight: 900, color: textPri, margin: 0 }}>
                {period === "daily" ? "Daily" : "Monthly"} Records
              </h3>
              <p style={{ fontSize: 12, color: textSec, margin: 0 }}>Historical top 3 snapshots</p>
            </div>
          </div>

          {recordsLoading ? (
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              {[1, 2, 3].map(i => (
                <div key={i} style={{ height: 60, background: surface, borderRadius: 14, border: `1px solid ${border}`, animation: "pulse 1.4s ease-in-out infinite" }} />
              ))}
            </div>
          ) : records.length === 0 ? (
            <div style={{ background: surface, border: `1px solid ${border}`, borderRadius: 18, padding: "32px", textAlign: "center", color: textSec, transition: "background 0.3s" }}>
              <div style={{ fontSize: 40, marginBottom: 10 }}>📋</div>
              <p style={{ fontWeight: 700, color: textPri, marginBottom: 4 }}>No records yet</p>
              <p style={{ fontSize: 13 }}>Records are automatically saved at the end of each {period === "daily" ? "day" : "month"}.</p>
            </div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              {records.map((rec) => (
                <div
                  key={rec.period_key}
                  style={{ background: surface, border: `1px solid ${border}`, borderRadius: 16, overflow: "hidden", transition: "background 0.3s" }}
                >
                  {/* Record header row */}
                  <button
                    onClick={() => setExpandedRecord(expandedRecord === rec.period_key ? null : rec.period_key)}
                    style={{
                      width: "100%", display: "flex", alignItems: "center", justifyContent: "space-between",
                      padding: "14px 18px", background: "none", border: "none", cursor: "pointer",
                      fontFamily: "'Nunito', sans-serif",
                    }}
                  >
                    <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                      <div style={{ width: 38, height: 38, background: D ? "#253347" : "#f0fdf4", borderRadius: 10, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 18 }}>
                        {period === "daily" ? "📅" : "📆"}
                      </div>
                      <div style={{ textAlign: "left" }}>
                        <p style={{ fontSize: 14, fontWeight: 800, color: textPri, margin: 0 }}>{formatPeriodLabel(rec.period_key)}</p>
                        <p style={{ fontSize: 12, color: textSec, margin: 0 }}>{rec.top3?.length || 0} winners recorded</p>
                      </div>
                    </div>
                    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                      {/* Mini avatar strip */}
                      <div style={{ display: "flex", gap: -4 }}>
                        {(rec.top3 || []).slice(0, 3).map((e, i) => (
                          <div key={i} style={{
                            width: 28, height: 28, borderRadius: "50%",
                            background: MEDALS[i]?.gradient || "#64748b",
                            border: `2px solid ${surface}`,
                            display: "flex", alignItems: "center", justifyContent: "center",
                            fontSize: 12, color: "white", fontWeight: 800,
                            marginLeft: i > 0 ? -8 : 0,
                          }}>
                            {initials(e.name)}
                          </div>
                        ))}
                      </div>
                      {expandedRecord === rec.period_key
                        ? <ChevronUp size={16} color={textSec} />
                        : <ChevronDown size={16} color={textSec} />}
                    </div>
                  </button>

                  {/* Expanded top 3 */}
                  <AnimatePresence>
                    {expandedRecord === rec.period_key && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.2 }}
                        style={{ overflow: "hidden" }}
                      >
                        <div style={{ borderTop: `1px solid ${border}`, padding: "12px 18px", display: "flex", flexDirection: "column", gap: 8 }}>
                          {(rec.top3 || []).map((entry, i) => (
                            <div key={entry.user_id || i} style={{ display: "flex", alignItems: "center", gap: 12, padding: "10px 12px", background: surface2, borderRadius: 12, transition: "background 0.3s" }}>
                              <div style={{ width: 36, height: 36, borderRadius: "50%", background: MEDALS[i]?.gradient || "#64748b", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 16, color: "white", fontWeight: 900, flexShrink: 0 }}>
                                {initials(entry.name)}
                              </div>
                              <div style={{ flex: 1 }}>
                                <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                                  <span style={{ fontSize: 14, fontWeight: 700, color: textPri }}>{entry.name}</span>
                                  <span style={{ fontSize: 13 }}>{MEDALS[i]?.label}</span>
                                </div>
                                <span style={{ fontSize: 12, color: textSec }}>Rank #{entry.rank}</span>
                              </div>
                              <div style={{ textAlign: "right" }}>
                                <p style={{ fontSize: 14, fontWeight: 800, color: "#f59e0b", margin: 0 }}>⚡ {entry.total_gp} GP</p>
                              </div>
                            </div>
                          ))}
                          {(!rec.top3 || rec.top3.length === 0) && (
                            <p style={{ fontSize: 13, color: textSec, textAlign: "center", padding: "8px 0" }}>No participants this period</p>
                          )}
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              ))}
            </div>
          )}
        </motion.div>
      </main>

      <style>{`
        @keyframes pulse { 0%,100%{opacity:1} 50%{opacity:0.45} }
        @keyframes spin  { to{transform:rotate(360deg)} }
      `}</style>
    </div>
  );
};

export default LeaderboardPage;
